import React, { useState } from "react";
import { Sparkles, FlaskConical, Wand2, X, ArrowRight } from "lucide-react";

const KEY = "gv_builder_welcome_v1";

// A calm, once-per-creator "start here" card for the builder. Dismissible and remembered.
// Gives first-timers three unmistakable next steps instead of a wall of panels.
export default function BuilderWelcome({ title, onAmazing, onTestPlay, onDirector }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(KEY) === "1"; } catch { return false; }
  });
  if (dismissed) return null;
  const close = () => { try { localStorage.setItem(KEY, "1"); } catch { /* ignore */ } setDismissed(true); };
  const go = (fn) => { close(); fn && fn(); };

  const steps = [
    { key: "amazing", icon: Sparkles, label: "Make it amazing", desc: "One tap — the AI polishes visuals, feel & audio.", fn: onAmazing,
      cls: "from-fuchsia-500/15 to-amber-400/10 border-fuchsia-500/40 hover:border-fuchsia-500/70" },
    { key: "test", icon: FlaskConical, label: "Test play", desc: "Jump straight into your game and try it out.", fn: onTestPlay,
      cls: "from-emerald-500/15 to-transparent border-emerald-500/40 hover:border-emerald-500/70" },
    { key: "director", icon: Wand2, label: "Ask the AI Director", desc: "Describe a big idea — it plans & builds it step by step.", fn: onDirector,
      cls: "from-sky-500/15 to-transparent border-sky-500/40 hover:border-sky-500/70" },
  ];

  return (
    <div className="mx-4 lg:mx-6 mt-4 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 sm:p-5 relative overflow-hidden" data-testid="builder-welcome">
      <div className="pointer-events-none absolute -top-16 -right-10 w-64 h-64 rounded-full bg-[hsl(var(--primary))]/10 blur-3xl" />
      <button onClick={close} aria-label="Dismiss" data-testid="builder-welcome-dismiss"
        className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-full text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--secondary))] transition-colors">
        <X className="w-4 h-4" />
      </button>
      <div className="relative">
        <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--primary))] flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> Welcome to the studio
        </p>
        <h2 className="font-display text-xl sm:text-2xl font-bold mt-1 mb-1">
          {title ? <>“{title}” is ready — where to next?</> : "Your game is ready — where to next?"}
        </h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4 max-w-2xl">
          Everything on the left tweaks your game; the chat on the right lets you just <em>say</em> what you want. Not sure where to begin? Pick one:
        </p>
        <div className="grid gap-2.5 sm:grid-cols-3">
          {steps.map((s) => (
            <button key={s.key} onClick={() => go(s.fn)} data-testid={`builder-welcome-${s.key}`}
              className={`text-left rounded-xl border bg-gradient-to-br p-3.5 transition-colors group ${s.cls}`}>
              <div className="flex items-center justify-between">
                <s.icon className="w-5 h-5 text-[hsl(var(--foreground))]" />
                <ArrowRight className="w-4 h-4 text-[hsl(var(--muted-foreground))] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="text-sm font-bold mt-2">{s.label}</div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] leading-snug mt-0.5">{s.desc}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
